import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate } from 'react-router';
import { paths } from 'src/paths';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthorizationDetails } from 'src/redux/actions/therapyPlanning';
import moment from 'moment';
import { Box } from '@mui/system';

const { therapyPlanning } = paths;

const AuthorizationDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patientInfo = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const authDetails = useSelector(
    (state: any) => state.getAuthorizationDetailsReducer?.response?.data
  );
  const patientSelectedLevelList = patientInfo?.levels?.filter(
    (item: any) => item?.level === authDetails?.authLevelDTO?.level
  );
  const patientSelectedLevel = patientSelectedLevelList?.length > 0 && patientSelectedLevelList[0];


  const getAllAuthDetailsResponse = useSelector(
    (state: any) => state?.getAuthorizationDetailsReducer
  );

  useEffect(() => {
    dispatch(
      getAuthorizationDetails({
        patientId: patientInfo?.uuid,
      })
    );
  }, []);

  //

  const SkeletonLoader = () => {
    return (
      <Card>
        <Grid
          display={'flex'}
          flexDirection={'column'}
        >
          <Box flex={1}>
            <CardHeader
              sx={{ pb: 0 }}
              title="General Information"
            />
            <CardContent sx={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
              {[1, 2, 3].map((res: number) => (
                <Typography
                  width={'45%'}
                  height={'10px'}
                  key={res}
                  variant="h4"
                >
                  <Skeleton animation="pulse" />
                </Typography>
              ))}
            </CardContent>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
          />
          <Box flex={1}>
            <CardHeader
              sx={{ pb: 0 }}
              title="Authorization Details"
            />
            <CardContent sx={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
              {[1, 2, 3].map((res: number) => (
                <Typography
                  width={'45%'}
                  height={'10px'}
                  key={res}
                  variant="h4"
                >
                  <Skeleton animation="pulse" />
                </Typography>
              ))}
            </CardContent>
          </Box>
          <Box flex={1}>
            <CardHeader
              sx={{ pb: 0 }}
              title="Additional Info"
            />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2].map((res: number) => (
                <Typography
                  width={'45%'}
                  key={res}
                  variant="h5"
                >
                  <Skeleton animation="pulse" />
                </Typography>
              ))}
            </CardContent>
          </Box>
        </Grid>
      </Card>
    );
  };

  return (
    <>
      {getAllAuthDetailsResponse?.loading ? (
        <SkeletonLoader />
      ) : (
        <Card sx={{ marginTop: '1.5rem' }}>
          <Stack gap="1.5rem">
            <Typography
              sx={{ margin: '10px 10px' }}
              variant="h5"
            >
              {patientInfo?.isAuthorized
                ? 'Authorization information is required by the payer'
                : 'Authorization is not required by the payer'}
            </Typography>
            <Stack
              alignItems={'center'}
              flexDirection="row"
              justifyContent="space-between"
              sx={{ margin: '1rem 1rem 0 1rem' }}
            >
              <Typography variant="h6">General Information</Typography>
              <Button
                onClick={() =>
                  navigate(
                    `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editAuthorization}`,
                    {
                      state: {
                        selectedPatient: patientInfo,
                      },
                    }
                  )
                }
                startIcon={<EditIcon />}
              >
                Edit Details
              </Button>
            </Stack>
            <Stack
              sx={{
                width: '60%',
                margin: '0rem 1rem',
              }}
              flexDirection="row"
              justifyContent={'space-between'}
            >
              <Stack>
                <Typography variant="subtitle2">Authorization number</Typography>
                <Typography
                  sx={{ color: '#6C737F' }}
                  variant="body2"
                >
                  {authDetails?.number || '-'}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Start date</Typography>
                <Typography
                  sx={{ color: '#6C737F' }}
                  variant="body2"
                >
                  {!!authDetails?.startDate
                    ? moment(authDetails?.startDate).format('DD MMM, YYYY')
                    : '-'}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">End date</Typography>
                <Typography
                  sx={{ color: '#6C737F' }}
                  variant="body2"
                >
                  {!!authDetails?.endDate
                    ? moment(authDetails?.endDate).format('DD MMM, YYYY')
                    : '-'}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ border: '1px solid #F2F4F7' }} />
            <Stack
              sx={{ padding: '1rem' }}
              gap="1.5rem"
            >
              <Typography variant="h6">Authorization Details</Typography>
              {patientInfo?.payerManageLevel ? (
                <Stack
                  flexDirection="row"
                  gap="1rem"
                >
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Authorization Level</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.authLevelDTO?.level || '-'}
                    </Typography>
                  </Stack>
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Approved disciplines</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.authLevelDTO?.ptDiscipline ? 'PT, ' : null}
                      {authDetails?.authLevelDTO?.otDiscipline ? 'OT, ' : null}
                      {authDetails?.authLevelDTO?.slpDiscipline ? 'SLP' : null}

                      {!authDetails?.authLevelDTO?.ptDiscipline &&
                      !authDetails?.authLevelDTO?.otDiscipline &&
                      !authDetails?.authLevelDTO?.slpDiscipline
                        ? '-'
                        : null}
                    </Typography>
                  </Stack>
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Frequency</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.authLevelDTO?.frequency || '0'}
                    </Typography>
                  </Stack>
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Minimum number of minutes</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.authLevelDTO
                        ? `${authDetails?.authLevelDTO?.noOfMinutes || '0'} / ${
                            patientSelectedLevel?.minutesType || 'Week'
                          }`
                        : '-'}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Stack
                  flexDirection="row"
                  sx={{ width: '75%', marginTop: '1rem' }}
                  gap="1rem"
                >
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Approved disciplines</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.payerAuthDisciplines?.disciplines?.map(
                        (desc: string, index: number) => (
                          <>
                            {desc}
                            {!(
                              authDetails?.payerAuthDisciplines?.disciplines?.length - 1 ===
                              index
                            ) && ', '}
                          </>
                        )
                      ) || '-'}
                    </Typography>
                  </Stack>
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Approved CPT codes</Typography>
                    <Typography
                      sx={{ color: '#6C737F' }}
                      variant="body2"
                    >
                      {authDetails?.payerAuthDisciplines?.cptCodes?.map(
                        (desc: string, index: number) => (
                          <>
                            {desc}
                            {!(authDetails?.payerAuthDisciplines?.cptCodes?.length - 1 === index) &&
                              ', '}
                          </>
                        )
                      ) || '-'}
                    </Typography>
                  </Stack>
                  <Stack flex={2.5}>
                    <Typography variant="subtitle2">Approved number of units</Typography>
                    <Typography
                      sx={{ color: '#6C737F', display: 'flex', justifyContent: 'start' }}
                      variant="body2"
                    >
                      {authDetails?.payerAuthDisciplines?.noOfApprovedMinutes || '-'}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Divider sx={{ border: '1px solid #F2F4F7' }} />
            <Typography
              sx={{ margin: '0rem 1rem' }}
              variant="h6"
            >
              Additional Info
            </Typography>
            <Typography
              sx={{ color: '#6C737F', margin: '0rem 1rem 1.5rem 1rem' }}
              variant="body2"
            >
              {authDetails?.additionalInfo || '-'}
            </Typography>
          </Stack>
        </Card>
      )}
    </>
  );
};

export default AuthorizationDetails;
